import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch, getBackendUrl } from "../../../api";
import { trackEvent } from "../../../analytics/posthog";
import { onWordSaveChange } from "../learning/hooks/useWordSave";
import { ExtraPracticePanel } from "../extra-practice-panel";
import PremiumModal from "../../../components/PremiumModal";
import s from "./HistoryPanel.module.css";

const BACKEND = getBackendUrl();

/* ── Story Card (dark image-style) ── */
function StoryCard({ story, index, onLearn, scrollRef, cardState, onPremiumClick }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (scrollRef && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollRef]);

  const isPremiumLocked = story.is_premium_required && cardState === "locked";
  const isLocked = cardState === "locked";

  const cardClass = [
    s.card,
    cardState === "completed" ? s.cardCompleted : "",
    isLocked && !isPremiumLocked ? s.cardLocked : "",
    isPremiumLocked ? s.cardPremiumClickable : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      ref={cardRef}
      className={cardClass}
      style={{ animationDelay: `${0.05 + index * 0.04}s` }}
      onClick={isPremiumLocked ? onPremiumClick : undefined}
    >
      {cardState === "completed" && (
        <div className={s.completedBadge}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Completed
        </div>
      )}
      {isPremiumLocked && (
        <div className={s.premiumBadge}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          Premium
        </div>
      )}
      {isLocked && !isPremiumLocked && (
        <div className={s.lockedOverlay}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </div>
      )}
      {story.has_avatar ? (
        <div className={`${s.cardAvatarWrap} ${isPremiumLocked ? s.premiumGrayInner : ""}`}>
          <img
            className={s.cardAvatar}
            src={`${BACKEND}/api/v1/admin/story-avatar/${story.story_id}?t=${Date.now()}`}
            alt={`Illustration for: ${story.topic_in_english}`}
          />
        </div>
      ) : (
        <div className={`${s.cardPlaceholder} ${isPremiumLocked ? s.premiumGrayInner : ""}`}>
          <svg className={s.cardPlaceholderIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
          </svg>
        </div>
      )}
      <div className={`${s.cardContent} ${isPremiumLocked ? s.premiumGrayInner : ""}`}>
        <h3 className={s.cardTitle}>{story.topic_in_swedish}</h3>
        <p className={s.cardDesc}>{story.topic_in_english}</p>
        <div className={s.cardFooter}>
          {cardState === "completed" && (
            <button
              className={s.learnAgainBtn}
              onClick={() => onLearn(story.story_id)}
            >
              Learn Again
            </button>
          )}
          {cardState === "active" && (
            <button
              className={s.learnBtn}
              onClick={() => onLearn(story.story_id)}
            >
              {story.user_status === "in_progress" ? "Continue Learning" : "Start Learning"}
            </button>
          )}
        </div>
      </div>
      {isPremiumLocked && (
        <div className={s.premiumFooter}>
          <button className={s.upgradeBtnCard} onClick={onPremiumClick}>
            Upgrade to Unlock
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Practice Due Card — "Ember" style ── */
function PracticeDueCard({ storyCount, wordCount, onClick }) {
  const parts = [];
  if (wordCount > 0) parts.push(`${wordCount} word${wordCount !== 1 ? "s" : ""}`);
  if (storyCount > 0) parts.push(`${storyCount} ${storyCount === 1 ? "story" : "stories"}`);
  const total = wordCount + storyCount;

  return (
    <button className={s.practiceDueCard} onClick={onClick}>
      <div className={s.practiceDueGlow} />
      <div className={s.practiceDueCountWrap}>
        <div className={s.practiceDueOrb} />
        <span className={s.practiceDueCount}>{total}</span>
      </div>
      <div className={s.practiceDueBody}>
        <div className={s.practiceDueLabelRow}>
          <svg className={s.practiceDueFlame} viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z" />
            <path d="M12 18a3.75 3.75 0 0 0 .495-7.468 5.99 5.99 0 0 0-1.925 3.547 5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z" />
          </svg>
          <span className={s.practiceDueLabel}>Ready to review</span>
        </div>
        <span className={s.practiceDueDesc}>{parts.join(" · ")}</span>
      </div>
      <div className={s.practiceDueCta}>
        <span>Review now</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>
    </button>
  );
}

/* ── Main Panel ── */
export default function HistoryPanel({ onLearn, userEmail, stories, loading = false, error = "" }) {
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceDue, setPracticeDue] = useState(null); // { stories: N, flashcards: N }

  const fetchPracticeDueCount = useCallback(async () => {
    try {
      const res = await apiFetch("/api/v1/daily-practice/count");
      if (res.ok) {
        const data = await res.json();
        setPracticeDue(data);
      }
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchPracticeDueCount();
  }, [fetchPracticeDueCount]);

  // Re-fetch when words are saved/removed from any story
  useEffect(() => onWordSaveChange(fetchPracticeDueCount), [fetchPracticeDueCount]);

  const hasPracticeDue = practiceDue && (practiceDue.stories > 0 || practiceDue.flashcards > 0);

  if (practiceMode) {
    return (
      <ExtraPracticePanel
        onBackToStories={() => {
          setPracticeMode(false);
          fetchPracticeDueCount();
        }}
        onPracticeComplete={fetchPracticeDueCount}
      />
    );
  }

  if (loading || stories === null) {
    return (
      <div className={s.panel}>
        <div className={s.emptyState}>
          <div className={s.spinner} />
          <p className={s.emptyDesc}>Loading your stories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.panel}>
        <div className={s.emptyState}>
          <div className={s.errorIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <h3 className={s.emptyTitle}>Something went wrong</h3>
          <p className={s.emptyDesc}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.panel}>
      {/* Content */}
      {stories.length === 0 ? (
        <div className={s.emptyState}>
          <svg className={s.emptyIcon} width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
          </svg>
          <h3 className={s.emptyTitle}>Your library is empty</h3>
          <p className={s.emptyDesc}>Generate your first Swedish story to begin learning.</p>
        </div>
      ) : (
        <div className={s.grid}>
          {(() => {
            const activeIndex = stories.findIndex(st => st.user_status !== "completed" && !st.is_premium_required);
            // Find where not_started stories begin (insert practice card before them)
            const notStartedIndex = stories.findIndex(st => st.user_status === "not_started");
            const practiceInsertIndex = notStartedIndex === -1 ? stories.length : notStartedIndex;

            const elements = [];
            stories.forEach((story, i) => {
              // Insert PracticeDueCard before the first not_started story
              if (i === practiceInsertIndex && hasPracticeDue) {
                elements.push(
                  <PracticeDueCard
                    key="practice-due"
                    storyCount={practiceDue.stories}
                    wordCount={practiceDue.flashcards}
                    onClick={() => {
                      trackEvent("practice_due_opened", { stories: practiceDue.stories, words: practiceDue.flashcards });
                      setPracticeMode(true);
                    }}
                  />
                );
              }

              let cardState;
              if (story.user_status === "completed") cardState = "completed";
              else if (story.user_status === "in_progress") cardState = "active";
              else if (i === activeIndex) cardState = "active";
              else cardState = "locked";

              elements.push(
                <StoryCard
                  key={story.story_id}
                  story={story}
                  index={i}
                  onLearn={onLearn}
                  scrollRef={i === activeIndex}
                  cardState={cardState}
                  onPremiumClick={() => setShowPremiumModal(true)}
                />
              );
            });

            // If all stories are completed/in_progress (no not_started), insert at end
            if (practiceInsertIndex === stories.length && hasPracticeDue) {
              elements.push(
                <PracticeDueCard
                  key="practice-due"
                  storyCount={practiceDue.stories}
                  wordCount={practiceDue.flashcards}
                  onClick={() => {
                    trackEvent("practice_due_opened", { stories: practiceDue.stories, words: practiceDue.flashcards });
                    setPracticeMode(true);
                  }}
                />
              );
            }

            return elements;
          })()}
          <p className={s.moreLabel}>More stories coming soon...</p>
        </div>
      )}

      {showPremiumModal && (
        <PremiumModal onClose={() => setShowPremiumModal(false)} userEmail={userEmail} />
      )}
    </div>
  );
}
